// Tracking Meta / GTM : 3 conversions funnel (inscription, transfert, commande).
// Source de vérité navigateur = dataLayer (GTM Pixel). event_id stable pour dédup CAPI / GTM.

export function getMetaSignupEventId(userId) {
    return userId ? `signup_${userId}` : undefined;
}

export function getMetaTransferEventId(conversationId) {
    return conversationId ? `transfer_${conversationId}` : undefined;
}

export function getMetaPurchaseEventId(orderCode) {
    return orderCode ? `purchase_${orderCode}` : undefined;
}

const PURCHASE_TRACKED_PREFIX = 'mls_tracked_purchase_';
const purchaseTrackedInMemory = new Set();

/** Purchase déjà envoyé au dataLayer pour cette commande (mémoire + localStorage). */
export function isPurchaseAlreadyTracked(orderCode) {
    if(!orderCode) {
        return false;
    }
    if(purchaseTrackedInMemory.has(orderCode)) {
        return true;
    }
    if(typeof window === 'undefined') {
        return false;
    }
    try {
        return localStorage.getItem(`${PURCHASE_TRACKED_PREFIX}${orderCode}`) === '1';
    } catch (err) {
        return false;
    }
}

export function markPurchaseTracked(orderCode) {
    if(!orderCode) {
        return;
    }
    purchaseTrackedInMemory.add(orderCode);
    if(typeof window === 'undefined') {
        return;
    }
    try {
        localStorage.setItem(`${PURCHASE_TRACKED_PREFIX}${orderCode}`, '1');
    } catch (err) {
        // quota / mode privé : la garde mémoire suffit pour la même page
    }
}

/**
 * Réserve l'envoi purchase pour orderCode (1 seul fire navigateur / commande).
 * @returns {boolean} false si déjà tracké — ne pas repousser dataLayer / Pixel.
 */
export function claimPurchaseTracking(orderCode) {
    if(!orderCode || isPurchaseAlreadyTracked(orderCode)) {
        return false;
    }
    markPurchaseTracked(orderCode);
    return true;
}

function readCookie(name) {
    if(typeof document === 'undefined' || !document.cookie) {
        return null;
    }
    const match = document.cookie.match(new RegExp(`(?:^|; )${name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1')}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
}

/** Site d'arrivée externe. Un referrer du même host (navigation interne) n'écrase pas le premier. */
function externalLandingReferrer() {
    if(typeof document === 'undefined' || !document.referrer || typeof window === 'undefined') {
        return undefined;
    }
    try {
        const referrerHost = new URL(document.referrer).hostname.toLowerCase();
        const ownHost = (window.location.hostname || '').toLowerCase();
        if(!referrerHost || referrerHost === ownHost) {
            return undefined;
        }
        return String(document.referrer).slice(0, 1024);
    } catch (err) {
        return undefined;
    }
}

function pickClickParam(params, key, max) {
    const value = params?.[key];
    if(value === undefined || value === null || value === '') {
        return undefined;
    }
    return String(value).slice(0, max);
}

/** Query router + URL courante (fallback si search vide, ex. checkout). */
function mergeSearchParams(search = {}) {
    const fromArg = search && typeof search === 'object' && !Array.isArray(search) ? search : {};
    let fromUrl = {};
    if(typeof window !== 'undefined' && window.location?.search) {
        fromUrl = Object.fromEntries(new URLSearchParams(window.location.search));
    }
    return { ...fromUrl, ...fromArg };
}

/** Snapshot navigateur pour CAPI (cookies Pixel + UTM + click IDs + UA). */
export function getBrowserMetaAttribution(search = {}) {
    if(typeof window === 'undefined') {
        return null;
    }

    const payload = {};
    const params = mergeSearchParams(search);

    const fbclid = pickClickParam(params, 'fbclid', 256);
    if(fbclid) {
        payload.fbclid = fbclid;
    }
    const gclid = pickClickParam(params, 'gclid', 256);
    if(gclid) {
        payload.gclid = gclid;
    }
    const gbraid = pickClickParam(params, 'gbraid', 256);
    if(gbraid) {
        payload.gbraid = gbraid;
    }
    const wbraid = pickClickParam(params, 'wbraid', 256);
    if(wbraid) {
        payload.wbraid = wbraid;
    }
    if(params.utm_source) {
        payload.utmSource = String(params.utm_source).slice(0, 128);
    }
    if(params.utm_medium) {
        payload.utmMedium = String(params.utm_medium).slice(0, 128);
    }
    if(params.utm_campaign) {
        payload.utmCampaign = String(params.utm_campaign).slice(0, 128);
    }
    const referrer = externalLandingReferrer();
    if(referrer) {
        payload.referrer = referrer;
    }

    const fbp = readCookie('_fbp');
    const fbc = readCookie('_fbc');
    if(fbp) {
        payload.fbp = String(fbp).slice(0, 256);
    }
    if(fbc) {
        payload.fbc = String(fbc).slice(0, 256);
    } else if(payload.fbclid) {
        payload.fbc = `fb.1.${Date.now()}.${payload.fbclid}`.slice(0, 256);
    }

    if(window.location?.href) {
        payload.eventSourceUrl = String(window.location.href).slice(0, 1024);
    }
    if(typeof navigator !== 'undefined' && navigator.userAgent) {
        payload.clientUserAgent = String(navigator.userAgent).slice(0, 512);
    }

    return Object.keys(payload).length ? payload : null;
}

function ensureDataLayer() {
    if(typeof window === 'undefined') {
        return null;
    }
    window.dataLayer = window.dataLayer || [];
    return window.dataLayer;
}

/**
 * Pousse un event dataLayer avec event_id stable.
 * GTM mappe déjà :
 * - sign_up → CompleteRegistration
 * - generate_lead → Lead
 * - purchase → Purchase
 * Ne pas appeler fbq en parallèle (sinon double fire avec des eventID GTM différents).
 */
export function trackConversion(eventName, payload = {}) {
    const dataLayer = ensureDataLayer();
    if(!dataLayer) {
        return;
    }

    const { eventId, ...rest } = payload || {};
    dataLayer.push({
        event: eventName,
        ...(eventId ? { event_id: eventId } : {}),
        ...rest,
    });
}

export function trackSignup(user) {
    const userId = user?._id || user?.id;
    if(!userId) {
        return;
    }
    // user_data enrichit le matching Meta (EMQ) : mêmes chemins que GTM (user_data.firstName, etc.)
    trackConversion('sign_up', {
        eventId: getMetaSignupEventId(userId),
        method: 'email',
        user_id: String(userId),
        user_data: {
            email: user.email || '',
            firstName: user.firstName || '',
            lastName: user.lastName || '',
            phone_number: user.phone || '',
        },
    });
}

export function trackTransfer(conversation, extra = {}) {
    const conversationId = conversation?._id || conversation?.id;
    if(!conversationId) {
        return;
    }
    const providers = conversation?.options?.providers || extra.providers || [];
    trackConversion('generate_lead', {
        eventId: getMetaTransferEventId(conversationId),
        content_type: 'conversation_transfer',
        content_id: String(conversationId),
        content_category: Array.isArray(providers) ? providers.join(',') : String(providers || ''),
        ...extra,
    });
}

export function trackPurchase(order, extra = {}) {
    if(!order?.code || !claimPurchaseTracking(order.code)) {
        return false;
    }
    trackConversion('purchase', {
        eventId: getMetaPurchaseEventId(order.code),
        transaction_id: order.code,
        currency: order.currency || extra.currency,
        value: parseFloat(order.totals?.totalTTC ?? extra.value) || 0,
        ...extra,
    });
    return true;
}
